import React, { useEffect, useState, useRef } from "react";
import { Button, Col, Modal, Row, Space, List, Divider, Input, Select, Tag, Checkbox, InputNumber } from "antd";
import { useAsyncAxios, utilAxiosExcelFileWithAuth, utilAxiosWithAuth } from "../../utils/customAxios";
import { ColumnsType } from "antd/lib/table";
import { CarOutlined } from "@ant-design/icons";

import * as Request from "../../commons/request";
import * as Types from "../../commons/typeDTO";
import * as Strings from "../../commons/string";
import * as Common from "../../commons/common";
import * as Utils from "../../utils/utils";

import moment from "moment";
import DataTable from "../../components/DataTable";
import RentScheduler from "../../components/RentScheduler";
import RentDetail from "../../components/RentDetail";

import styles from "./ReservationManagement.module.css";
import StatsCard from "../../components/StatsCard";
import RentUserDetail from "../../components/RentUserDetail";
import RentImageViewer from "../../components/RentImageViewer";
import RentPaymentViewer from "../../components/RentPaymentViewer";
import SearchDate from "../../components/SearchDate";
import ModalAdminReservation, { RentAdminReservation } from "../../components/ModalAdminReservation";
import { useAuthState } from "../../provider/AuthProvider";
import { AlertType, useAlert } from "../../provider/AlertProvider";

function ReservationManagement() {
    const userDetails = useAuthState();
    const alert = useAlert();

    const [searchStartDate, setSearchStartDate] = useState(moment().subtract(1, "M").startOf("month").format(Common.FORMAT_DATE));
    const [searchEndDate, setSearchEndDate] = useState(moment().add(1, "M").endOf("month").format(Common.FORMAT_DATE));
    const detailRef = useRef<HTMLDivElement>(null);

    const [rents, setRents] = useState<Array<Types.RentDto>>([]);
    const [currentRent, setCurrentRent] = useState<Types.RentDto>();
    const [selectedEvent, setSelectedEvent] = useState<number>();
    const [openModalCancelReservation, setOpenModalCancelReservation] = useState(false);
    const [cancelRent, setCancelRent] = useState<Types.RentDto>();
    const [cancelRents, setCancelRents] = useState<Array<number>>();

    const [totalRentCarCount, setTotalRentCarCount] = useState(0);
    const [totalActiveRentCarCount, setTotalActiveRentCarCount] = useState(0);
    const [totalRepairingRentCarCount, setTotalRepairingRentCarCount] = useState(0);
    const [totalWaitingRentCarCount, setTotalWaitingRentCarCount] = useState(0);

    const [rentsByStartDate, setRentsByStartDate] = useState<Utils.RentsByDate>();
    const [rentsByEndDate, setRentsByEndDate] = useState<Utils.RentsByDate>();

    const [startRentsOnDate, setStartRentsOnDate] = useState<Array<Types.RentDto>>([]);
    const [endRentsOnDate, setEndRentsOnDate] = useState<Array<Types.RentDto>>([]);

    const [searchRentStatus, setSearchRentStatus] = useState<string>();
    const [searchKeywords, setSearchKeywords] = useState<string>();

    const [paymentItems, setPaymentItems] = useState<Array<Types.RentPaymentItemDto>>([]);

    const [openModalAdminReservation, setOpenModalAdminReservation] = useState(false);

    const [noPenalty, setNoPenalty] = useState(true);
    const [penaltyPercent, setPenaltyPercent] = useState(0);
    const [penaltyAmount, setPenaltyAmount] = useState(0);

    const [resetSearch, setResetSearch] = useState(false);

    const [showAdminReservation, setShowAdminReservation] = useState(true);
    const [doneAddPaidItem, setDoneAddPaidItem] = useState(true);
    const [b2bs, setB2bs] = useState<Array<Types.B2bDto>>();

    useEffect(() => {
        requestGetRentCars();
        requestGetPaymentItems();
        requestGetB2bs();
    }, []);

    const requestAxiosGetRentCars = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_CARS_ALL_URL);
        return response.data;
    };

    const {
        loading: loadingGetRentCars,
        error: errorGetRentCars,
        data: resultGetRentCars,
        execute: requestGetRentCars,
    } = useAsyncAxios(requestAxiosGetRentCars);

    useEffect(() => {
        if (resultGetRentCars === null) return;
        console.log("resultGetRentCars", resultGetRentCars);

        const rentCars = resultGetRentCars.rentCars;
        setTotalRentCarCount(rentCars.length);
        setTotalActiveRentCarCount(rentCars.filter((car: Types.RentCarDto) => car.repairing !== true && car.waiting !== true).length);
        setTotalRepairingRentCarCount(rentCars.filter((car: Types.RentCarDto) => car.repairing === true).length);
        setTotalWaitingRentCarCount(rentCars.filter((car: Types.RentCarDto) => car.waiting === true).length);
    }, [resultGetRentCars]);

    useEffect(() => {
        if (errorGetRentCars === null) return;
        console.log("errorGetRentCars", errorGetRentCars);
    }, [errorGetRentCars]);

    const requestAxiosGetRents = async (rentStatus?: string, keywords?: string) => {
        let response;
        const updatedB2bs = ["ROLE_ADMIN", "ROLE_MANAGER"].includes(userDetails?.user?.authority)
            ? [null, ...(b2bs ?? []).map((b2b) => b2b.b2bId)]
            : (b2bs ?? []).map((b2b) => b2b.b2bId);

        console.log("updatedB2bs", updatedB2bs);

        if ((rentStatus !== undefined && rentStatus.length > 0) || (keywords !== undefined && keywords.length > 0)) {
            response = await utilAxiosWithAuth().get(Request.RENT_ALL_URL, {
                params: {
                    b2bs: updatedB2bs,
                    startDate: searchStartDate,
                    endDate: searchEndDate,
                    rentStatus: rentStatus,
                    keyWord: keywords,
                    showAdminReservation: showAdminReservation,
                },
            });
        } else {
            response = await utilAxiosWithAuth().get(Request.RENT_ALL_URL, {
                params: {
                    b2bs: updatedB2bs,
                    startDate: searchStartDate,
                    endDate: searchEndDate,
                    showAdminReservation: showAdminReservation,
                },
            });
        }
        return response.data;
    };
    const { loading: loadingGetRents, error: errorGetRents, data: resultGetRents, execute: requestGetRents } = useAsyncAxios(requestAxiosGetRents);

    useEffect(() => {
        if (resultGetRents === null) return;
        setRents(resultGetRents.rents);
        setRentsByStartDate(Utils.convertRentByStartDate(resultGetRents.rents, showAdminReservation));
        setRentsByEndDate(Utils.convertRentByEndDate(resultGetRents.rents, showAdminReservation));

        if (currentRent !== undefined) {
            const currentRents = resultGetRents.rents.filter((rent: Types.RentDto) => rent.rentId === currentRent.rentId);
            if (currentRents.length > 0) setCurrentRent(currentRents[0]);
        }

        setStartRentsOnDate(
            resultGetRents.rents.filter(
                (rent: Types.RentDto) =>
                    rent.startDate === selectedDate &&
                    rent.rentStatus !== "CANCELED" &&
                    (showAdminReservation || rent.user.email !== "support@alpamotors.com")
            )
        );
        setEndRentsOnDate(
            resultGetRents.rents.filter(
                (rent: Types.RentDto) =>
                    rent.endDate === selectedDate &&
                    rent.rentStatus !== "CANCELED" &&
                    (showAdminReservation || rent.user.email !== "support@alpamotors.com")
            )
        );
        setDoneAddPaidItem(true);
    }, [resultGetRents]);

    useEffect(() => {
        if (errorGetRents === null) return;
        console.log("errorGetRents", errorGetRents);
    }, [errorGetRents]);

    const requestAxiosGetB2bs = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_B2B_LIST_URL);
        return response.data;
    };

    const { loading: loadingGetB2bs, error: errorGetB2bs, data: resultGetB2bs, execute: requestGetB2bs } = useAsyncAxios(requestAxiosGetB2bs);

    useEffect(() => {
        if (resultGetB2bs === null) return;
        console.log("resultGetB2bs", resultGetB2bs);
        setB2bs(resultGetB2bs.rents);
    }, [resultGetB2bs]);

    useEffect(() => {
        if (errorGetB2bs === null) return;
        console.log("errorGetB2bs", errorGetB2bs);
    }, [errorGetB2bs]);

    useEffect(() => {
        requestGetRents();
    }, [b2bs]);

    useEffect(() => {
        if (rents === undefined) return;
        const canceledRent = rents.filter((rent) => rent.rentStatus === "CANCELED");
        if (canceledRent.length > 0) {
            setCancelRents(canceledRent.map((rent) => rent.rentId));
        }
    }, [rents]);

    const requestAxiosGetPaymentItems = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_PAYMENT_ITEMS_URL);
        return response.data;
    };

    const {
        loading: loadingGetPaymentItems,
        error: errorGetPaymentItems,
        data: resultGetPaymentItems,
        execute: requestGetPaymentItems,
    } = useAsyncAxios(requestAxiosGetPaymentItems);

    useEffect(() => {
        if (resultGetPaymentItems === null) return;
        console.log("resultGetPaymentItems", resultGetPaymentItems);
        setPaymentItems(resultGetPaymentItems.rentPaymentItems);
    }, [resultGetPaymentItems]);

    useEffect(() => {
        if (errorGetPaymentItems === null) return;
        console.log("errorGetPaymentItems", errorGetPaymentItems);
    }, [errorGetPaymentItems]);

    const onChangedSearchDate = (type: string, value: string) => {
        if (type === "startDate") setSearchStartDate(moment(value).format(Common.FORMAT_DATE));
        else if (type === "endDate") setSearchEndDate(moment(value).format(Common.FORMAT_DATE));
    };

    const onSearhRents = () => {
        requestGetRents(searchRentStatus, searchKeywords);
    };

    const onSearhRefresh = () => {
        setSearchStartDate(moment().format(Common.FORMAT_DATE));
        setSearchEndDate(moment().add(1, "M").endOf("month").format(Common.FORMAT_DATE));
        setSearchKeywords(undefined);
        setSearchRentStatus(undefined);

        setResetSearch(true);
    };

    useEffect(() => {
        if (resetSearch === false) return;

        onSearhRents();

        setResetSearch(false);
    }, [resetSearch]);

    const onAdminReservation = () => {
        setOpenModalAdminReservation(true);
    };

    const onChangeRentAdminReservation = (rentInfo: RentAdminReservation) => {
        requestAdminReservation(rentInfo);
    };

    const requestAxiosAdminReservation = async (rentInfo: RentAdminReservation) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_ADD_ADMIN_URL, null, { params: rentInfo });
        return response.data;
    };

    const {
        loading: loadingAdminReservation,
        error: errorAdminReservation,
        data: resultAdminReservation,
        execute: requestAdminReservation,
    } = useAsyncAxios(requestAxiosAdminReservation);

    useEffect(() => {
        if (resultAdminReservation === null) return;
        console.log("resultAdminReservation", resultAdminReservation);

        alert.setAlert(AlertType.SUCCESS, "", "예약이 완료되었습니다.");

        requestGetRents();
    }, [resultAdminReservation]);

    useEffect(() => {
        if (errorAdminReservation === null) return;
        console.log("errorAdminReservation", errorAdminReservation);
    }, [errorAdminReservation]);

    const onAdminAdjustments = async () => {
        await utilAxiosExcelFileWithAuth()
            .get(Request.RENT_ADJUSTMENTS_URL, { params: { startDate: searchStartDate, endDate: searchEndDate } })
            .then((response) => {
                console.log("response", response);

                const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers["content-type"] }));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", searchStartDate + "_" + searchEndDate + "_정산내역.xlsx");
                // link.download = decodeURIComponent(response.headers["content-disposition"].split("filename=")[1].replace(/"/g, ""));
                document.body.appendChild(link);
                link.click();
            })
            .catch((error) => {
                console.log("error", error);
                alert.setAlert(error.code, "Error", error.message);
            });
    };

    const getPayment = (rent: Types.RentDto) => {
        return rent?.rentPayments?.find((payment) => payment?.rentPaymentType === "RENT");
    };

    const getCancelPercent = (startDate: string) => {
        const days = moment(startDate).diff(moment().subtract(1, "day"), "days");
        let percent = 0;

        if (days >= 3) {
            if (days <= 5) {
                percent = 20;
            } else if (days <= 10) {
                percent = 50;
            } else {
                percent = 100;
            }

            return 100 - percent;
        }
        return 100;
    };

    const handleCancelReservationModelOk = () => {
        setOpenModalCancelReservation(false);

        if (cancelRent !== undefined) {
            const cancelAmount = penaltyAmount;

            const returnAmount = (getPayment(cancelRent)?.amount || 0) - cancelAmount;

            requestCancelPayment(getPayment(cancelRent)?.rentPaymentId, returnAmount);
        }
    };

    const requestAxiosCancelPayment = async (rentPaymentId: number, cancelAmount: number) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_CANCEL_URL + rentPaymentId + "?cancelAmount=" + cancelAmount);
        return response.data;
    };

    const {
        loading: loadingCancelPayment,
        error: errorCancelPayment,
        data: resultCancelPayment,
        execute: requestCancelPayment,
    } = useAsyncAxios(requestAxiosCancelPayment);

    useEffect(() => {
        if (resultCancelPayment === null) return;
        console.log("resultCancelPayment", resultCancelPayment);

        requestCancelRent(resultCancelPayment.rentPayment.rent.rentId);
    }, [resultCancelPayment]);

    useEffect(() => {
        if (errorCancelPayment === null) return;
        console.log("errorPaymentCarComplete", errorCancelPayment);
    }, [errorCancelPayment]);

    useEffect(() => {
        cancelRent && setPenaltyPercent(getCancelPercent(cancelRent.startDate));
    }, [cancelRent]);

    useEffect(() => {
        !openModalCancelReservation && setNoPenalty(true);
    }, [openModalCancelReservation]);

    useEffect(() => {
        if (cancelRent) {
            if (noPenalty === true) {
                setPenaltyAmount(0);
            } else {
                const insuranceAmount = cancelRent?.rentInsurance?.price || 0;
                const paidItemAmount = cancelRent?.rentPaidItems?.reduce((total, item) => total + item.price, 0) || 0;
                const rentalPrice = (getPayment(cancelRent)?.amount || 0) - insuranceAmount - paidItemAmount;

                setPenaltyAmount((rentalPrice / 100) * penaltyPercent);
            }
        }
    }, [noPenalty, penaltyPercent]);

    const onCancelRent = (rent: Types.RentDto) => {
        setOpenModalCancelReservation(true);
        setCancelRent(rent);
    };

    const requestAxiosCancelRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_URL + rentId + Request.RENT_CANCEL_URL);
        return response.data;
    };

    const {
        loading: loadingCancelRent,
        error: errorCancelRent,
        data: resultCancelRent,
        execute: requestCancelRent,
    } = useAsyncAxios(requestAxiosCancelRent);

    useEffect(() => {
        if (resultCancelRent === null) return;
        console.log("resultCancelRent", resultCancelRent);

        alert.setAlert(AlertType.SUCCESS, "", "예약이 취소되었습니다.");

        setCurrentRent(undefined);
        requestGetRents();
    }, [resultCancelRent]);

    useEffect(() => {
        if (errorCancelRent === null) return;
        console.log("errorCancelRent", errorCancelRent);
    }, [errorCancelRent]);

    const onUseRent = (rent: Types.RentDto) => {
        requestUseRent(rent.rentId);
    };

    const requestAxiosUseRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId + Request.RENT_USE_URL);
        return response.data;
    };

    const { loading: loadingUseRent, error: errorUseRent, data: resultUseRent, execute: requestUseRent } = useAsyncAxios(requestAxiosUseRent);

    useEffect(() => {
        if (resultUseRent === null) return;
        console.log("resultUseRent", resultUseRent);
        requestGetRents();
    }, [resultUseRent]);

    useEffect(() => {
        if (errorUseRent === null) return;
        console.log("errorUseRent", errorUseRent);
    }, [errorUseRent]);

    const onUseRentCancel = (rent: Types.RentDto) => {
        requestUseRentCancel(rent.rentId);
    };

    const requestAxiosUseRentCancel = async (rentId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId + Request.RENT_USE_URL + Request.RENT_CANCEL_URL);
        return response.data;
    };

    const {
        loading: loadingUseRentCancel,
        error: errorUseRentCancel,
        data: resultUseRentCancel,
        execute: requestUseRentCancel,
    } = useAsyncAxios(requestAxiosUseRentCancel);

    useEffect(() => {
        if (resultUseRentCancel === null) return;
        console.log("resultUseRentCancel", resultUseRentCancel);
        requestGetRents();
    }, [resultUseRentCancel]);

    useEffect(() => {
        if (errorUseRentCancel === null) return;
        console.log("errorUseRentCancel", errorUseRentCancel);
    }, [errorUseRentCancel]);

    const requestAxiosReturnRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId + Request.RENT_RETURN_URL);
        return response.data;
    };

    const {
        loading: loadingReturnRent,
        error: errorReturnRent,
        data: resultReturnRent,
        execute: requestReturnRent,
    } = useAsyncAxios(requestAxiosReturnRent);

    useEffect(() => {
        if (resultReturnRent === null) return;
        console.log("resultReturnRent", resultReturnRent);
        requestGetRents();
    }, [resultReturnRent]);

    useEffect(() => {
        if (errorReturnRent === null) return;
        console.log("errorReturnRent", errorReturnRent);
    }, [errorReturnRent]);

    const [arrivedRent, setArrivedRent] = useState<Types.RentDto>();

    const onArrivedRent = (rent: Types.RentDto) => {
        requestArrivedRent(rent.rentId);
    };

    const requestAxiosArrivedRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId + Request.RENT_ARRIVE_URL);
        return response.data;
    };

    const {
        loading: loadingArrivedRent,
        error: errorArrivedRent,
        data: resultArrivedRent,
        execute: requestArrivedRent,
    } = useAsyncAxios(requestAxiosArrivedRent);

    useEffect(() => {
        if (resultArrivedRent === null) return;
        console.log("resultArrivedRent", resultArrivedRent);
        setArrivedRent(resultArrivedRent.rent);
        requestReturnRent(resultArrivedRent.rent.rentId);

        requestGetRents();
    }, [resultArrivedRent]);

    useEffect(() => {
        if (errorArrivedRent === null) return;
        console.log("errorArrivedRent", errorArrivedRent);
    }, [errorArrivedRent]);

    useEffect(() => {
        if (arrivedRent) {
            addBatteryPayment(arrivedRent);
            addDelayedArrivePayment(arrivedRent);
        }
    }, [arrivedRent]);

    const onChangeRentCar = (rent: Types.RentDto, rentCarId: number) => {
        requestChangeRentCar(rent.rentId, rentCarId);
    };

    const requestAxiosChangeRentCar = async (rentId: number, rentCarId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, { usedRentCar: { rentCarId: rentCarId } });
        return response.data;
    };

    const {
        loading: loadingChangeRentCar,
        error: errorChangeRentCar,
        data: resultChangeRentCar,
        execute: requestChangeRentCar,
    } = useAsyncAxios(requestAxiosChangeRentCar);

    useEffect(() => {
        if (resultChangeRentCar === null) return;
        console.log("resultChangeRentCar", resultChangeRentCar);
        requestGetRents();
    }, [resultChangeRentCar]);

    useEffect(() => {
        if (errorChangeRentCar === null) return;
        console.log("errorChangeRentCar", errorChangeRentCar);
    }, [errorChangeRentCar]);

    const onChangeRentDetail = (rent: Types.RentDto, memo: string, checkedUsagePolicy: boolean, checkedExitPolicy: boolean) => {
        requestChangeRentDetail(rent.rentId, memo, checkedUsagePolicy, checkedExitPolicy);
    };

    const requestAxiosChangeRentDetail = async (rentId: number, memo: string, checkedUsagePolicy: boolean, checkedExitPolicy: boolean) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, {
            memo: memo,
            checkedUsagePolicy: checkedUsagePolicy,
            checkedExitPolicy: checkedExitPolicy,
        });
        return response.data;
    };

    const {
        loading: loadingChangeRentDetail,
        error: errorChangeRentDetail,
        data: resultChangeRentDetail,
        execute: requestChangeRentDetail,
    } = useAsyncAxios(requestAxiosChangeRentDetail);

    useEffect(() => {
        if (resultChangeRentDetail === null) return;
        console.log("resultChangeRentDetail", resultChangeRentDetail);
        requestGetRents();
    }, [resultChangeRentDetail]);

    useEffect(() => {
        if (errorChangeRentDetail === null) return;
        console.log("errorChangeRentDetail", errorChangeRentDetail);
    }, [errorChangeRentDetail]);

    const onUpdatePaidItem = (rent: Types.RentDto, paidItemIds: Array<number>, payments?: Array<Types.RentPaidItemDto>) => {
        setDoneAddPaidItem(false);
        requestUpdatePaidItem(rent.rentId, paidItemIds);

        const tablePayment = currentRent?.rentPayments.find((payment: Types.RentPaymentDto) =>
            payment?.rentPaymentItem?.rentPaymentItemName?.includes("테이블")
        );
        const beddingPayment = currentRent?.rentPayments.find((payment: Types.RentPaymentDto) =>
            payment?.rentPaymentItem?.rentPaymentItemName?.includes("침구")
        );

        payments?.forEach((item: Types.RentPaidItemDto) => {
            if ((item?.rentPaidItemType === "TABLE_CHAIR" && tablePayment) || (item?.rentPaidItemType === "BEDDING_SET" && beddingPayment)) {
                const payment: any = {
                    rentPaymentId: item?.rentPaidItemType === "TABLE_CHAIR" ? tablePayment?.rentPaymentId : beddingPayment?.rentPaymentId,
                    basicPrice: item?.price,
                    amount: item?.price,
                    rentPaymentItem: {
                        rentPaymentItemName: item?.rentPaidItemName,
                    },
                };
                requestUpdatePaidItemPayment(payment);
            } else if ((item?.rentPaidItemType === "TABLE_CHAIR" && !tablePayment) || (item?.rentPaidItemType === "BEDDING_SET" && !beddingPayment)) {
                const payment: any = {
                    rent: currentRent,
                    rentPaymentType: "PAID_ITEM",
                    basicPrice: item?.price,
                    paymentMethod: "OFFLINE_BANK",
                    adjustedPrice: 0,
                    amount: item?.price,
                    rentPaymentItem: {
                        rentPaymentItemName: item?.rentPaidItemName,
                    },
                };
                requestAddPaidItemPayment(payment);
            }
        });
    };

    const requestAxiosUpdatePaidItem = async (rentId: number, paidItemIds: Array<number>) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, {
            rentPaidItems: paidItemIds.map((paidItemId) => {
                return { rentPaidItemId: paidItemId };
            }),
        });
        return response.data;
    };

    const {
        loading: loadingUpdatePaidItem,
        error: errorUpdatePaidItem,
        data: resultUpdatePaidItem,
        execute: requestUpdatePaidItem,
    } = useAsyncAxios(requestAxiosUpdatePaidItem);

    useEffect(() => {
        if (resultUpdatePaidItem === null) return;
        console.log("resultUpdatePaidItem", resultUpdatePaidItem);

        const paidItems = resultUpdatePaidItem?.rent?.rentPaidItems;
        const paidItemsPayments = resultUpdatePaidItem?.rent?.rentPayments.filter(
            (payment: Types.RentPaymentDto) => payment?.rentPaymentType === "PAID_ITEM"
        );
        const tableItem = paidItems.find((item: Types.RentPaidItemDto) => item?.rentPaidItemType === "TABLE_CHAIR");
        const beddingItem = paidItems.find((item: Types.RentPaidItemDto) => item?.rentPaidItemType === "BEDDING_SET");
        const tablePayment = paidItemsPayments.find((payment: Types.RentPaymentDto) =>
            payment?.rentPaymentItem?.rentPaymentItemName?.includes("테이블")
        );
        const beddingPayment = paidItemsPayments.find((payment: Types.RentPaymentDto) =>
            payment?.rentPaymentItem?.rentPaymentItemName?.includes("침구")
        );

        if (!tableItem && tablePayment) {
            requestDeletePayment(tablePayment);
        }
        if (!beddingItem && beddingPayment) {
            requestDeletePayment(beddingPayment);
        }
    }, [resultUpdatePaidItem]);

    useEffect(() => {
        if (errorUpdatePaidItem === null) return;
        console.log("errorUpdatePaidItem", errorUpdatePaidItem);
    }, [errorUpdatePaidItem]);

    const requestAxiosAddPaidItemPayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_ADD_URL, payment);
        return response.data;
    };

    const {
        loading: loadingAddPaidItemPayment,
        error: errorAddPaidItemPayment,
        data: resultAddPaidItemPayment,
        execute: requestAddPaidItemPayment,
    } = useAsyncAxios(requestAxiosAddPaidItemPayment);

    useEffect(() => {
        if (resultAddPaidItemPayment === null) return;
        console.log("resultAddPaidItemPayment", resultAddPaidItemPayment);
        requestGetRents();
    }, [resultAddPaidItemPayment]);

    useEffect(() => {
        if (errorAddPaidItemPayment === null) return;
        console.log("errorAddPaidItemPayment", errorAddPaidItemPayment);
    }, [errorAddPaidItemPayment]);

    const requestAxiosUpdatePaidItemPayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_PAYMENT_URL + payment.rentPaymentId, payment);
        return response.data;
    };

    const {
        loading: loadingUpdatePaidItemPayment,
        error: errorUpdatePaidItemPayment,
        data: resultUpdatePaidItemPayment,
        execute: requestUpdatePaidItemPayment,
    } = useAsyncAxios(requestAxiosUpdatePaidItemPayment);

    useEffect(() => {
        if (resultUpdatePaidItemPayment === null) return;
        console.log("resultUpdatePaidItemPayment", resultUpdatePaidItemPayment);
        requestGetRents();
    }, [resultUpdatePaidItemPayment]);

    useEffect(() => {
        if (errorUpdatePaidItemPayment === null) return;
        console.log("errorUpdatePaidItemPayment", errorUpdatePaidItemPayment);
    }, [errorUpdatePaidItemPayment]);

    const addBatteryPayment = (rentInfo: Types.RentDto, ignoreExistPayment?: boolean) => {
        if (
            ignoreExistPayment !== true &&
            rentInfo.rentPayments.find((rentPayment) =>
                rentPayment.rentPaymentItem?.rentPaymentCategory?.rentPaymentCategoryName.includes("배터리 미충전")
            )
        ) {
            console.log("penalty battery already exist");
            return false;
        }

        if (rentInfo.startBatterySoc && rentInfo.endBatterySoc) {
            if (rentInfo.endBatterySoc >= Common.BATTERY_MAX_LIMIT) return false;

            // const startSoc = rentInfo.startBatterySoc > Common.BATTERY_MAX_LIMIT ? Common.BATTERY_MAX_LIMIT : rentInfo.startBatterySoc;
            const startSoc = rentInfo.startBatterySoc;

            if (startSoc - rentInfo.endBatterySoc >= 1) {
                const batteryPaymentItem = paymentItems.find((paymentItem) =>
                    paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("배터리 미충전")
                );
                if (batteryPaymentItem === null || batteryPaymentItem === undefined) return false;

                const penaltySoc = Math.floor(startSoc - rentInfo.endBatterySoc);
                const payment: any = {
                    rent: rentInfo,
                    rentPaymentType: "PENALTY",
                    rentPaymentItem: batteryPaymentItem,
                    basicPrice: penaltySoc * Common.BATTERY_PENALTY,
                    paymentMethod: "OFFLINE_BANK",
                    adjustedPrice: 0,
                    amount: penaltySoc * Common.BATTERY_PENALTY,
                };

                requestAddBatteryPayment(payment);
                return true;
            }
        }

        return false;
    };

    const requestAxiosAddBatteryPayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_ADD_URL, payment);
        return response.data;
    };

    const {
        loading: loadingAddBatteryPayment,
        error: errorAddBatteryPayment,
        data: resultAddBatteryPayment,
        execute: requestAddBatteryPayment,
    } = useAsyncAxios(requestAxiosAddBatteryPayment);

    useEffect(() => {
        if (resultAddBatteryPayment === null) return;
        console.log("resultAddBatteryPayment", resultAddBatteryPayment);
        requestGetRents();
    }, [resultAddBatteryPayment]);

    useEffect(() => {
        if (errorAddBatteryPayment === null) return;
        console.log("errorAddBatteryPayment", errorAddBatteryPayment);
    }, [errorAddBatteryPayment]);

    const addDelayedArrivePayment = (rentInfo: Types.RentDto) => {
        if (
            rentInfo.rentPayments.find((rentPayment) =>
                rentPayment.rentPaymentItem?.rentPaymentCategory?.rentPaymentCategoryName.includes("반납 지연")
            )
        ) {
            console.log("penalty delay arrive already exist");
            return false;
        }

        if (rentInfo.arrivedDate !== null && rentInfo.arrivedDate !== undefined && rentInfo.usedDate !== null && rentInfo.usedDate !== undefined) {
            const originalArrivedDate = moment(rentInfo.endDate + Common.DEFAULT_ARRIVED_TIME);
            const realArrivedDate = moment(Utils.utcToLocalTime(rentInfo.arrivedDate, Common.FORMAT_DATE_TIME));

            const minutes = realArrivedDate.diff(originalArrivedDate, "minutes");

            let delayedPaymentItem = null;
            if (minutes > 0 && minutes <= 60) {
                delayedPaymentItem = paymentItems.find(
                    (paymentItem) =>
                        paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("반납 지연") &&
                        paymentItem.rentPaymentItemName.includes("1시간 이내 지연 시")
                );
            } else if (minutes > 60 && minutes <= 120) {
                delayedPaymentItem = paymentItems.find(
                    (paymentItem) =>
                        paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("반납 지연") &&
                        paymentItem.rentPaymentItemName.includes("1시간 초과 2시간 내 지연 시")
                );
            } else if (minutes > 120) {
                delayedPaymentItem = paymentItems.find(
                    (paymentItem) =>
                        paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("반납 지연") &&
                        paymentItem.rentPaymentItemName.includes("2시간 초과 지연 시")
                );
            }

            if (delayedPaymentItem === null || delayedPaymentItem === undefined) return false;

            const payment: any = {
                rent: rentInfo,
                rentPaymentType: "PENALTY",
                rentPaymentItem: delayedPaymentItem,
                basicPrice: Utils.getPaymentItemAmount(rentInfo, delayedPaymentItem),
                paymentMethod: "OFFLINE_BANK",
                adjustedPrice: 0,
                amount: Utils.getPaymentItemAmount(rentInfo, delayedPaymentItem),
            };

            requestAddDelayedArrivePayment(payment);

            return true;
        }

        return false;
    };

    const requestAxiosAddDelayedArrivePayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_ADD_URL, payment);
        return response.data;
    };

    const {
        loading: loadingAddDelayedArrivePayment,
        error: errorAddDelayedArrivePayment,
        data: resultAddDelayedArrivePayment,
        execute: requestAddDelayedArrivePayment,
    } = useAsyncAxios(requestAxiosAddDelayedArrivePayment);

    useEffect(() => {
        if (resultAddDelayedArrivePayment === null) return;
        console.log("resultAddDelayedArrivePayment", resultAddDelayedArrivePayment);
        requestGetRents();
    }, [resultAddDelayedArrivePayment]);

    useEffect(() => {
        if (errorAddDelayedArrivePayment === null) return;
        console.log("errorAddDelayedArrivePayment", errorAddDelayedArrivePayment);
    }, [errorAddDelayedArrivePayment]);

    const columns: ColumnsType<Types.RentDto> = [
        {
            align: "center",
            title: "번호",
            dataIndex: "num",
            key: "rentId",
            render: (item, record, index) => <>{rents && rents.indexOf(record) + 1}</>,
        },
        {
            align: "center",
            title: "예약일시",
            dataIndex: "createdDate",
            key: "createdDate",
            sorter: (a, b, sortOrder) => Utils.sortString(a.createdDate, b.createdDate, sortOrder),
            render: (_, row) => {
                return row.createdDate ? Utils.utcToLocalTime(row.createdDate, Common.FORMAT_DATE_TIME).substring(2, 16) : Strings.dash;
            },
        },
        {
            align: "center",
            title: "이용일",
            dataIndex: "startDate",
            key: "startDate",
            sorter: (a, b, sortOrder) => Utils.sortString(a.startDate.valueOf(), b.startDate.valueOf(), sortOrder),
            render: (_, row) => {
                return row.startDate ? row.startDate.substring(2) : Strings.dash;
            },
        },
        {
            align: "center",
            title: "반납일",
            dataIndex: "endDate",
            key: "endDate",
            sorter: (a, b, sortOrder) => Utils.sortString(a.endDate.valueOf(), b.endDate.valueOf(), sortOrder),
            render: (_, row) => {
                return row.endDate ? row.endDate.substring(2) : Strings.dash;
            },
        },
        {
            align: "center",
            title: "이름",
            dataIndex: "user",
            key: "user",
            sorter: (a, b, sortOrder) => Utils.sortString(a.user?.name.valueOf(), b.user?.name.valueOf(), sortOrder),
            render: (_, row) => {
                return row.user?.name ? row.user?.name : Strings.dash;
            },
        },
        {
            align: "center",
            title: "휴대전화번호",
            dataIndex: "phone",
            key: "phone",
            sorter: (a, b, sortOrder) => Utils.sortString(a.user?.phone.valueOf(), b.user?.phone.valueOf(), sortOrder),
            render: (_, row) => {
                return row.user?.phone ? row.user?.phone : Strings.dash;
            },
        },
        {
            align: "center",
            title: "차량번호",
            dataIndex: "carNumber",
            key: "carNumber",
            sorter: (a, b, sortOrder) => Utils.sortString(a.rentCar.car.carNumber.valueOf(), b.rentCar.car.carNumber.valueOf(), sortOrder),
            render: (_, row) => {
                if (row.rentCar?.car?.carNumber) {
                    return row.rentCar?.car?.carNumber;
                } else return Strings.dash;
            },
        },
        {
            align: "center",
            title: "예약번호",
            dataIndex: "rentNo",
            key: "rentNo",
            sorter: (a, b, sortOrder) => Utils.sortString(a.rentNo.valueOf(), b.rentNo.valueOf(), sortOrder),
            render: (_, row) => {
                if (row.rentNo) {
                    return row.rentNo;
                } else return Strings.dash;
            },
        },
        {
            align: "center",
            title: "다날주문번호",
            dataIndex: "danal",
            key: "danal",
            sorter: (a, b, sortOrder) => {
                const aPayment = Utils.getReservationPayment(a.rentPayments);
                const bPayment = Utils.getReservationPayment(b.rentPayments);

                return Utils.sortString(
                    aPayment !== null && aPayment.paymentImp !== null ? aPayment.paymentImp.impUid.valueOf() : Strings.dash.valueOf(),
                    bPayment !== null && bPayment.paymentImp !== null ? bPayment.paymentImp.impUid.valueOf() : Strings.dash.valueOf(),
                    sortOrder
                );
            },
            render: (_, row) => {
                if (row.rentPayments.find((item) => item.rentPaymentType === "RENT")?.paymentImp?.impUid !== undefined) {
                    return <span style={{ color: "#000" }}>{Utils.getReservationPayment(row.rentPayments)?.paymentImp?.impUid}</span>;
                } else return <Tag color="error">미결제</Tag>;
            },
        },
        {
            align: "center",
            title: "예약상태",
            dataIndex: "rentStatus",
            key: "rentStatus",
            sorter: (a, b, sortOrder) => {
                console.log(a, b);
                return Utils.sortString(Utils.getRentStatus(a)?.valueOf(), Utils.getRentStatus(b)?.valueOf(), sortOrder);
            },
            render: (_, row) => {
                return row.rentStatus === "DURING_PAYMENT" ? (
                    <span style={{ color: "red" }}>{Utils.getRentStatus(row)}</span>
                ) : (
                    Utils.getRentStatus(row)
                );
            },
        },
        {
            align: "center",
            title: "총 정산요금",
            dataIndex: "amount",
            key: "amount",
            sorter: (a, b, sortOrder) => {
                const aPayment = Utils.getAllPaidPaymentAmount(a.rentPayments);
                const bPayment = Utils.getAllPaidPaymentAmount(b.rentPayments);

                return Utils.sortString(String(aPayment).valueOf(), String(bPayment).valueOf(), sortOrder);
            },
            render: (_, row) => {
                return Utils.convertCurrencyKorean(Utils.getAllPaidPaymentAmount(row.rentPayments));
            },
        },
    ];

    useEffect(() => {
        if (selectedEvent !== undefined) {
            const selectedRents = rents.filter((rent) => rent.rentId === selectedEvent);
            if (selectedRents.length > 0) {
                setCurrentRent(selectedRents[0]);
            }
        }
    }, [selectedEvent]);

    const [selectedDate, setSelectedDate] = useState(moment().format(Common.FORMAT_DATE));

    useEffect(() => {
        setStartRentsOnDate(
            rents.filter(
                (rent) =>
                    rent.startDate === selectedDate &&
                    rent.rentStatus !== "CANCELED" &&
                    (showAdminReservation || rent.user.email !== "support@alpamotors.com")
            )
        );
        setEndRentsOnDate(
            rents.filter(
                (rent) =>
                    rent.endDate === selectedDate &&
                    rent.rentStatus !== "CANCELED" &&
                    (showAdminReservation || rent.user.email !== "support@alpamotors.com")
            )
        );
    }, [selectedDate]);

    const onClickSchedulerDate = (date: string) => {
        setSelectedDate(moment(date).format(Common.FORMAT_DATE));
    };

    const displayReturnPayment = () => {
        if (cancelRent) {
            const cancelAmount = penaltyAmount;

            const returnAmount = (getPayment(cancelRent)?.amount || 0) - cancelAmount;

            return (
                <>
                    <p>결제금액 : {Utils.convertCurrencyKorean(getPayment(cancelRent)?.amount || 0)}</p>
                    <p>
                        위약금 : {Utils.convertCurrencyKorean(cancelAmount)} &#40;{getCancelPercent(cancelRent.startDate)} %&#41;
                    </p>
                    <Checkbox checked={noPenalty} onChange={(e) => setNoPenalty(e.target.checked)}>
                        위약금 없음
                    </Checkbox>
                    <InputNumber
                        placeholder="위약금 비율"
                        disabled={noPenalty}
                        value={penaltyPercent}
                        onChange={(value) => value && setPenaltyPercent(value)}
                    ></InputNumber>
                    <p>환불금액 : {Utils.convertCurrencyKorean(returnAmount)}</p>
                </>
            );
        } else return null;
    };

    const onChangeRentBattery = (rent: Types.RentDto, startSoc: number | null, endSoc: number | null) => {
        console.log("onChangeRentBattery", rent, startSoc, endSoc);
        requestChangeRentBattery(rent.rentId, startSoc, endSoc);
    };

    const requestAxiosChangeRentBattery = async (rentId: number, startSoc: number | null, endSoc: number | null) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, { startBatterySoc: startSoc, endBatterySoc: endSoc });
        return response.data;
    };

    const {
        loading: loadingChangeRentBattery,
        error: errorChangeRentBattery,
        data: resultChangeRentBattery,
        execute: requestChangeRentBattery,
    } = useAsyncAxios(requestAxiosChangeRentBattery);

    const [batteryRent, setBatteryRent] = useState<Types.RentDto>();

    useEffect(() => {
        if (resultChangeRentBattery === null) return;
        console.log("resultChangeRentBattery", resultChangeRentBattery);
        // requestGetRents();

        const rentInfo = resultChangeRentBattery.rent as Types.RentDto;
        setBatteryRent(rentInfo);
        const batteryPayment = rentInfo.rentPayments.find((rentPayment) =>
            rentPayment.rentPaymentItem?.rentPaymentCategory?.rentPaymentCategoryName.includes("배터리 미충전")
        );

        if (batteryPayment) {
            requestDeletePayment(batteryPayment);
        } else {
            addBatteryPayment(rentInfo);
            requestGetRents();
        }
    }, [resultChangeRentBattery]);

    useEffect(() => {
        if (errorChangeRentBattery === null) return;
        console.log("errorChangeRentBattery", errorChangeRentBattery);
    }, [errorChangeRentBattery]);

    const requestAxiosDeletePayment = async (rentPayment: Types.RentPaymentDto) => {
        const response = await utilAxiosWithAuth().delete(Request.RENT_PAYMENT_URL + rentPayment.rentPaymentId);
        return response.data;
    };

    const {
        loading: loadingDeletePayment,
        error: errorDeletePayment,
        data: resultDeletePayment,
        execute: requestDeletePayment,
    } = useAsyncAxios(requestAxiosDeletePayment);

    useEffect(() => {
        if (resultDeletePayment === null) return;
        console.log("resultDeletePayment", resultDeletePayment);
        batteryRent && addBatteryPayment(batteryRent, true);
        requestGetRents();
    }, [resultDeletePayment]);

    useEffect(() => {
        if (errorDeletePayment === null) return;
        console.log("errorDeletePayment", errorDeletePayment);
    }, [errorDeletePayment]);

    const onDetailClick = () => {
        detailRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <div className="container">
            <Space size={30} direction="vertical" style={{ width: "100%" }}>
                <h3 className="title">예약 관리</h3>
                {userDetails?.user?.authority === "ROLE_B2B_MANAGER" ? (
                    <></>
                ) : (
                    <div>
                        <Row justify={"space-between"}>
                            <Col span={6}>
                                <Divider orientation="left">
                                    <span>지역</span>
                                </Divider>
                            </Col>
                        </Row>
                        <Row gutter={20}>
                            <Col xs={24} md={12} className="gutter-row">
                                <Button style={{ width: "100%" }}>경주</Button>
                            </Col>
                            <Col xs={24} md={12} className="gutter-row">
                                <Button style={{ width: "100%" }}>제주</Button>
                            </Col>
                        </Row>
                    </div>
                )}
                <div>
                    {userDetails?.user?.authority !== "ROLE_B2B_MANAGER" && (
                        <>
                            <Row justify={"space-between"}>
                                <Col span={6}>
                                    <Divider orientation="left">
                                        <span>예약 현황</span>
                                    </Divider>
                                </Col>
                            </Row>
                            <Row gutter={20}>
                                <Col xs={24} md={6} className="gutter-row">
                                    <StatsCard
                                        title={Strings.totalRentCarCount}
                                        image={<CarOutlined />}
                                        value={totalRentCarCount.toString()}
                                        unit={Strings.count_car}
                                    />
                                </Col>
                                <Col xs={24} md={6} className="gutter-row">
                                    <StatsCard
                                        title={Strings.totalActiveRentCarCount}
                                        image={<CarOutlined />}
                                        value={totalActiveRentCarCount.toString()}
                                        unit={Strings.count_car}
                                    />
                                </Col>
                                <Col xs={24} md={6} className="gutter-row">
                                    <StatsCard
                                        title={Strings.totalRepairingRentCarCount}
                                        image={<CarOutlined />}
                                        value={totalRepairingRentCarCount.toString()}
                                        unit={Strings.count_car}
                                    />
                                </Col>
                                <Col xs={24} md={6} className="gutter-row">
                                    <StatsCard
                                        title={Strings.totalWaitingRentCarCount}
                                        image={<CarOutlined />}
                                        value={totalWaitingRentCarCount.toString()}
                                        unit={Strings.count_car}
                                    />
                                </Col>
                            </Row>
                        </>
                    )}
                    <Row gutter={20} className={styles.calendar}>
                        <Col span={12}>
                            <RentScheduler startRents={rentsByStartDate} endRents={rentsByEndDate} onClickDate={onClickSchedulerDate} />
                        </Col>
                        <Col span={12}>
                            <h3>{moment(selectedDate).format(Common.FORMAT_DATE_KOREAN)}</h3>
                            <List
                                header={
                                    <div style={{ textAlign: "start" }}>
                                        {Strings.rent_return} : {endRentsOnDate.length}건
                                    </div>
                                }
                                dataSource={endRentsOnDate}
                                renderItem={(item) => {
                                    return <RentsOnDate item={item} isEnd={true} setSelectedEvent={setSelectedEvent} />;
                                }}
                            />
                            <List
                                header={
                                    <div style={{ textAlign: "start" }}>
                                        {Strings.rent_use} : {startRentsOnDate.length}건
                                    </div>
                                }
                                dataSource={startRentsOnDate}
                                renderItem={(item) => {
                                    return <RentsOnDate item={item} isEnd={false} setSelectedEvent={setSelectedEvent} />;
                                }}
                            />
                        </Col>
                    </Row>
                </div>

                <div>
                    <Row justify={"space-between"} align={"middle"}>
                        <Col span={userDetails?.user?.authority !== "ROLE_B2B_MANAGER" ? 6 : 24}>
                            <Divider orientation="left">
                                <span>예약</span>
                            </Divider>
                        </Col>
                        {userDetails?.user?.authority !== "ROLE_B2B_MANAGER" && (
                            <Col span={6} style={{ display: "flex", justifyContent: "flex-end" }}>
                                <Checkbox checked={showAdminReservation} onChange={(e) => setShowAdminReservation(e.target.checked)}>
                                    관리자 예약 보기
                                </Checkbox>
                            </Col>
                        )}
                    </Row>
                    <Space direction="vertical" size={20} style={{ width: "100%" }}>
                        <Space>
                            <SearchDate startDate={searchStartDate} endDate={searchEndDate} onSearch={onChangedSearchDate} />
                            <Select style={{ width: 150 }} placeholder="전체" allowClear value={searchRentStatus} onChange={setSearchRentStatus}>
                                <Select.Option value={"DURING_PAYMENT"}>결제중</Select.Option>
                                <Select.Option value={"RESERVED"}>예약완료</Select.Option>
                                <Select.Option value={"CANCELED"}>예약취소</Select.Option>
                                <Select.Option value={"IN_USE"}>이용중</Select.Option>
                                <Select.Option value={"ARRIVED"}>도착완료</Select.Option>
                                <Select.Option value={"COMPLETED"}>반납완료</Select.Option>
                                <Select.Option value={"NO_SHOW"}>노쇼</Select.Option>
                            </Select>
                            <Input value={searchKeywords} onChange={(e) => setSearchKeywords(e.target.value)} />
                            <Button onClick={onSearhRefresh}>검색 초기화</Button>
                            <Button onClick={onSearhRents}>검색</Button>
                            {userDetails?.user?.authority === "ROLE_ADMIN" && <Button onClick={onAdminReservation}>관리자 예약</Button>}
                            {userDetails?.user?.authority === "ROLE_ADMIN" && <Button onClick={onAdminAdjustments}>정산내역</Button>}
                        </Space>
                        <DataTable
                            hasScroll={false}
                            columns={columns}
                            rowKey="rentId"
                            dataSource={rents}
                            onRow={(value) => setCurrentRent(value)}
                            canceledRow={cancelRents}
                        />
                    </Space>
                </div>

                <div ref={detailRef}>
                    {currentRent && (
                        <RentDetail
                            rent={currentRent}
                            onCancelRent={onCancelRent}
                            onUseRent={onUseRent}
                            onUseRentCancel={onUseRentCancel}
                            onArrivedRent={onArrivedRent}
                            onChangeRentCar={onChangeRentCar}
                            onChangeRentDetail={onChangeRentDetail}
                            onUpdatePaidItem={onUpdatePaidItem}
                            loadingAddPaidItem={!doneAddPaidItem}
                        />
                    )}
                </div>
                {currentRent?.user && <RentUserDetail user={currentRent?.user} />}
                {currentRent?.additionalDriver && <RentUserDetail user={currentRent?.additionalDriver} additionalDriverMode={true} />}
                {currentRent && <RentImageViewer rent={currentRent} onChangeRentBattery={onChangeRentBattery} />}
                {currentRent && <RentPaymentViewer rent={currentRent} onUpdateRentInfo={() => requestGetRents()} />}

                {openModalCancelReservation && cancelRent && (
                    <Modal
                        centered
                        title="예약취소"
                        open={openModalCancelReservation}
                        onOk={handleCancelReservationModelOk}
                        okText="예약취소"
                        onCancel={() => {
                            setOpenModalCancelReservation(false);
                        }}
                        cancelText="이전"
                    >
                        {displayReturnPayment()}
                    </Modal>
                )}
            </Space>
            {openModalAdminReservation && (
                <ModalAdminReservation
                    open={openModalAdminReservation}
                    onChangedOpen={(open: boolean) => setOpenModalAdminReservation(open)}
                    onChangeRentInfo={onChangeRentAdminReservation}
                />
            )}
        </div>
    );
}

function RentsOnDate({ item, isEnd, setSelectedEvent }: { item: Types.RentDto; isEnd: boolean; setSelectedEvent: (selectedEvent: number) => void }) {
    const color = isEnd ? Common.COLOR_RETURN : item.rentCar.repairing === true ? Common.COLOR_ERROR : Common.COLOR_USE;
    const duringPaymentColor = item.rentStatus === "DURING_PAYMENT" ? "red" : "black";

    return (
        <List.Item>
            <Row>
                <Row style={{ width: "100%" }} justify={"space-between"} align={"middle"}>
                    <Col>
                        <Space size={8}>
                            <div style={{ width: 12, height: 12, backgroundColor: color, margin: 0 }} />
                            {item.rentCar?.car?.carType?.typeName} -
                            <span style={{ color: duringPaymentColor }}>
                                {item.rentCar.title?.includes("★") ? "[특가]" : item.rentCar.b2b ? "[B2B]" : ""}
                                {item.rentCar.car.carNumber}
                                {item.usedRentCar && "(" + item.usedRentCar.car.carNumber + ")"}
                            </span>
                            <div style={{ color: duringPaymentColor }}>
                                {item.user.name}_{item.user.phone.substring(item.user.phone.length - 4, item.user.phone.length)}
                            </div>
                        </Space>
                    </Col>
                </Row>
                <Row style={{ width: "100%" }} justify={"space-between"} align={"middle"}>
                    <Space size={8}>
                        <Col xs={24} md={12}>
                            <Button onClick={() => setSelectedEvent(item.rentId)}>예약 상세보기</Button>
                        </Col>
                    </Space>
                </Row>
            </Row>
        </List.Item>
    );
}
export default ReservationManagement;
